import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  where,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../firebaseConfig";
import "./ApprovedInvoices.css";
import ContinueJobPopup from "../components/ContinueJobPopup";
import {
  createContinuationJob,
  findJobForWO,
  getNextContinuedWO,
} from "../utils/continueJobUtils";
import {
  isRecurring,
  toDateKey,
  getCanonicalKeyForScheduledKey,
} from "../utils/recurringOccurrenceUtils";
import {
  allocateDepositsToInvoices,
  invoiceTotal as sharedInvoiceTotal,
  isInvoiceCovered,
} from "../utils/depositsInvoiceMatching";

/* ===========================================================================
   APPROVED INVOICES (Priority Pest Defense)

   Every invoice created by the "Complete Job & Create Invoice" form lands
   here as Approved or Draft. Nothing is emailed to a client until an admin
   reviews it on this page and presses Send Invoice. Sending:
     - emails the PDF (sendApprovedInvoiceEmail) with the photos chosen in the
       send dialog attached (selectedImageUrls),
     - marks the invoice Sent (sentAt / sentMethod / emailedAt),
     - moves the job to Sent Invoices on Send Invoices: one-time job ->
       status "Invoice Sent"; recurring job -> ONLY that visit's canonical
       date is added to invoicedDates (the series is never closed).
   =========================================================================== */

const PPD_LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/priority-backend.firebasestorage.app/o/logo-images%2Fprioritypestlogoblue.png?alt=media&token=98e00fc0-c3af-4a85-b109-a6c8b02544ac";

const COMPANY = {
  name: "Priority Pest Defense LLC",
  addressLine1: "46164 Westlake Dr.",
  addressLine2: "Suite 650001",
  cityStateZip: "Sterling, VA 20165",
  email: "Team@PriorityPestDefense.com",
  phone: "202-841-4186",
};

const TERMS_OPTIONS = ["Due Upon Invoice", "Due on receipt", "NET 15", "NET 30", "Net 60", "NONE"];
const productServiceOptions = ["Pest Control Service", "Work Order", "Contract", "Supplies", "Other"];
const vendorOptions = ["PPD", "Other"];
const SEND_METHODS = ["Email", "Mailed", "Hand delivered", "Client portal", "Fax", "Other"];

const RANGE_PRESETS = [
  { id: "today", label: "Today" },
  { id: "week", label: "This week" },
  { id: "last7", label: "Last 7 days" },
  { id: "month", label: "This month" },
  { id: "last30", label: "Last 30 days" },
  { id: "custom", label: "Custom" },
];

// ---------------------------------------------------------------------------
// small helpers
// ---------------------------------------------------------------------------
const numberValue = (value) => {
  const num = Number(String(value ?? "").replace(/[$,]/g, ""));
  return Number.isFinite(num) ? num : 0;
};

const todayKey = () => new Date().toISOString().slice(0, 10);

const formatMoney = (value) =>
  Number(numberValue(value)).toLocaleString("en-US", { style: "currency", currency: "USD" });

const withCurrent = (options, current) => {
  const value = String(current ?? "").trim();
  if (!value) return options;
  return options.some((o) => o.toLowerCase() === value.toLowerCase()) ? options : [...options, value];
};

/** Firestore Timestamp | ISO string | Date -> Date (or null). */
const toJsDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/* Local calendar day, not UTC - a 7pm Eastern send must count for today. */
const localDayKey = (value) => {
  const d = toJsDate(value);
  if (!d) return "";
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const daysAgoKey = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localDayKey(d);
};

const startOfWeekKey = () => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  return localDayKey(d);
};

const startOfMonthKey = () => {
  const d = new Date();
  d.setDate(1);
  return localDayKey(d);
};

const prettyDay = (key) => {
  if (!key) return "";
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (value) => {
  const d = toJsDate(value);
  return d ? d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "";
};

/* sentAt wins; emailedAt is the fallback for invoices emailed before sentAt existed. */
const sentTimestamp = (invoice) => invoice?.sentAt || invoice?.emailedAt || "";

const statusOf = (invoice) => {
  const raw = String(invoice?.status || "Draft").trim();
  if (raw.toLowerCase() === "sent") return "Sent";
  if (raw.toLowerCase() === "paid") return "Paid";
  return raw || "Draft";
};

const chemicalText = (products = []) =>
  (products || [])
    .map((item) => (item?.qty ? `${item.chemical} - ${item.qty}` : item?.chemical))
    .filter(Boolean)
    .join("\n");

/* invoice.images can hold { url, path, name } objects (current form) or plain
   URL strings (older invoices). Normalize for display / sending only - the
   stored shape is never rewritten unless the admin edits the list. */
const imageUrlOf = (item) =>
  typeof item === "string"
    ? item
    : item?.url || item?.fileURL || item?.fileUrl || item?.downloadURL || item?.imageUrl || item?.src || "";

const normalizeImages = (list) =>
  (Array.isArray(list) ? list : [])
    .map((item, index) => {
      const url = imageUrlOf(item);
      if (!url) return null;
      return {
        url,
        path: typeof item === "object" ? item.path || "" : "",
        name:
          (typeof item === "object" && (item.name || item.fileName || item.filename)) ||
          `Photo ${index + 1}`,
      };
    })
    .filter(Boolean);

const notesText = (notes) => {
  if (!notes) return "";
  if (Array.isArray(notes)) {
    return notes
      .map((n) => (typeof n === "string" ? n : n?.text || ""))
      .filter(Boolean)
      .join("\n");
  }
  return String(notes);
};

const cleanRows = (rows = []) =>
  (rows || []).map((row) => ({
    serviceDescription: row.serviceDescription || "",
    quantity: row.quantity === "" || row.quantity === undefined ? "1.00" : String(row.quantity),
    amount: numberValue(row.amount),
    total: numberValue(row.quantity || 1) * numberValue(row.amount),
  }));

const rowsTotal = (rows = []) =>
  (rows || []).reduce((sum, row) => sum + numberValue(row.quantity || 1) * numberValue(row.amount), 0);

const hasRows = (invoice) => Array.isArray(invoice?.serviceRows) && invoice.serviceRows.length > 0;

/** The line items an invoice prints - the same choice the PDF builder makes. */
const printableRows = (invoice) =>
  hasRows(invoice)
    ? invoice.serviceRows.map((row) => ({
        description: row.serviceDescription || "",
        quantity: row.quantity || "1",
        rate: numberValue(row.amount),
        amount:
          row.total !== undefined && row.total !== null
            ? numberValue(row.total)
            : numberValue(row.quantity || 1) * numberValue(row.amount),
      }))
    : [
        {
          description: invoice?.description || invoice?.jobDescription || "",
          quantity: invoice?.qty || invoice?.quantity || 1,
          rate: numberValue(invoice?.rate),
          amount: numberValue(invoice?.amount),
        },
      ];

const sourceLabel = (source) => {
  const s = String(source || "").toLowerCase();
  if (!s) return "";
  if (s === "check_in") return "from check-in";
  if (s === "check_out") return "from check-out";
  if (s === "completion") return "completion time";
  if (s === "admin") return "edited by admin";
  if (s.includes("manual")) return "entered manually";
  if (s.includes("schedule")) return "from schedule";
  return s;
};

/* ---------------------------------------------------------------------------
   Popup shell - portaled to <body> with self-contained inline positioning.
   layer "main" -> 999998/999999, layer "top" -> 1999998/1999999.
--------------------------------------------------------------------------- */
const AiPopup = ({ title, subtitle, onClose, width = 640, layer = "top", children }) => {
  const base = layer === "main" ? 999998 : 1999998;

  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0,0,0,0.55)",
          zIndex: base,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="ai-popup"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          margin: 0,
          width: `min(${width}px, calc(100vw - 24px))`,
          maxHeight: "90vh",
          overflowY: "auto",
          background: "#ffffff",
          color: "#0f172a",
          padding: 20,
          borderRadius: 14,
          boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
          textAlign: "left",
          boxSizing: "border-box",
          zIndex: base + 1,
        }}
      >
        <div className="ai-popup-head">
          <div>
            {title && <h3 className="ai-popup-title">{title}</h3>}
            {subtitle && <p className="ai-popup-sub">{subtitle}</p>}
          </div>
          {onClose && (
            <button type="button" className="ai-popup-x" onClick={onClose} aria-label="Close">
              ×
            </button>
          )}
        </div>
        {children}
      </div>
    </>,
    document.body
  );
};

const StatusPill = ({ status }) => (
  <span className={`ai-pill ai-pill--${String(status || "Draft").toLowerCase().replace(/\s+/g, "-")}`}>
    {status || "Draft"}
  </span>
);

const Field = ({ label, full, children, hint }) => (
  <div className={`ai-field${full ? " ai-field--full" : ""}`}>
    <label>{label}</label>
    {children}
    {hint && <span className="ai-hint">{hint}</span>}
  </div>
);

const ApprovedInvoices = () => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editedInvoice, setEditedInvoice] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [properties, setProperties] = useState([]);
  const [managementCompanies, setManagementCompanies] = useState([]);
  const [deposits, setDeposits] = useState([]);

  const [sendingInvoiceId, setSendingInvoiceId] = useState(null);
  const [selectedInvoiceForEmail, setSelectedInvoiceForEmail] = useState(null);
  const [pricingInvoice, setPricingInvoice] = useState(null);
  const [availableInvoiceImages, setAvailableInvoiceImages] = useState([]);
  const [selectedInvoiceImages, setSelectedInvoiceImages] = useState([]);
  const [loadingEmailImages, setLoadingEmailImages] = useState(false);
  const [invoiceEmailData, setInvoiceEmailData] = useState({
    accountingEmail: "",
    cc: "",
    bcc: "",
    subject: "",
    message: "",
    includePayLink: true,
  });

  /* Sent-invoice totals panel. */
  const [totalsRange, setTotalsRange] = useState("today");
  const [totalsFrom, setTotalsFrom] = useState(() => localDayKey(new Date()));
  const [totalsTo, setTotalsTo] = useState(() => localDayKey(new Date()));
  const [showDailyBreakdown, setShowDailyBreakdown] = useState(false);

  /* Record a send that did not go by email. */
  const [markSentInvoice, setMarkSentInvoice] = useState(null);
  const [markSentDate, setMarkSentDate] = useState("");
  const [markSentMethod, setMarkSentMethod] = useState("Mailed");
  const [markSentNote, setMarkSentNote] = useState("");
  const [markSentSaving, setMarkSentSaving] = useState(false);

  const [showPaidInvoices, setShowPaidInvoices] = useState(false);
  const [showSentInvoices, setShowSentInvoices] = useState(false);
  const [invoiceSort, setInvoiceSort] = useState({ key: "invoiceDate", direction: "desc" });
  const [approvedInvoiceSearch, setApprovedInvoiceSearch] = useState("");
  const [sentInvoiceSearch, setSentInvoiceSearch] = useState("");
  const [paidInvoiceSearch, setPaidInvoiceSearch] = useState("");

  const [invoicePaymentMode, setInvoicePaymentMode] = useState("single");
  const [invoiceBeingPaid, setInvoiceBeingPaid] = useState(null);
  const [paidVendor, setPaidVendor] = useState("PPD");
  const [paidVendorOther, setPaidVendorOther] = useState("");
  const [paidCheckNumber, setPaidCheckNumber] = useState("");
  const [paidCheckDate, setPaidCheckDate] = useState(todayKey());
  const [paidReference, setPaidReference] = useState("");
  const [paidCheckAmount, setPaidCheckAmount] = useState("");
  const [splitPaidRows, setSplitPaidRows] = useState([]);
  const [savingPayment, setSavingPayment] = useState(false);

  /* Continue: branch a new Active job off this invoice's work order. */
  const [continueInvoice, setContinueInvoice] = useState(null);
  const [continueSourceJob, setContinueSourceJob] = useState(null);
  const [continueNextWO, setContinueNextWO] = useState("");
  const [continueLoading, setContinueLoading] = useState(false);
  const [continueSaving, setContinueSaving] = useState(false);
  const [continueError, setContinueError] = useState("");

  const functions = getFunctions();

  // ---------------------------------------------------------------- lookups
  const findPropertyForInvoice = (invoice, propertiesList = properties) =>
    propertiesList.find(
      (p) =>
        p.propertyName === invoice.siteName ||
        p.siteName === invoice.siteName ||
        p.propertyName === invoice.propertyName ||
        p.siteName === invoice.propertyName
    );

  const getPropertyCityStateZip = (property = {}, invoice = {}) => {
    if (invoice.cityStateZip) return invoice.cityStateZip;
    const city = invoice.city || property?.city || "";
    const state = invoice.state || property?.state || "";
    const zip = invoice.zip || property?.zip || "";
    return [city, state, zip].filter(Boolean).join(", ");
  };

  const getManagementName = (invoice, property) =>
    invoice.management ||
    property?.management ||
    invoice.managementCompany ||
    property?.managementCompany ||
    "";

  const getAutoAccountingEmail = (invoice, propertiesList, managementList) => {
    const property = findPropertyForInvoice(invoice, propertiesList);
    if (!property) {
      return invoice.accountingEmail || invoice.clientEmail || invoice.managerEmail || "";
    }
    const managementName = property.management || "";
    if (managementName) {
      const management = managementList.find((m) => m.management === managementName);
      return (
        property.accountingEmail ||
        management?.accountingEmail ||
        management?.billingEmail ||
        invoice.accountingEmail ||
        invoice.clientEmail ||
        invoice.managerEmail ||
        ""
      );
    }
    return (
      property.accountingEmail ||
      property.clientEmail ||
      invoice.accountingEmail ||
      invoice.clientEmail ||
      invoice.managerEmail ||
      property.managerEmail ||
      ""
    );
  };

  const getAutoClientEmail = (invoice, propertiesList, managementList) => {
    const property = findPropertyForInvoice(invoice, propertiesList);
    if (!property) return invoice.clientEmail || "";
    const managementName = property.management || "";
    if (managementName) {
      const management = managementList.find((m) => m.management === managementName);
      return (
        management?.billingEmail ||
        property.managerEmail ||
        property.clientEmail ||
        invoice.clientEmail ||
        ""
      );
    }
    return property.clientEmail || invoice.clientEmail || "";
  };

  const getManagementForInvoice = (invoice) => {
    const managementName = invoice.management || invoice.managementCompany || "";
    if (!managementName) return null;
    return managementCompanies.find(
      (m) =>
        m.management === managementName ||
        m.managementCompany === managementName ||
        m.companyName === managementName ||
        m.name === managementName
    );
  };

  const findJobForInvoice = async (invoice) => {
    if (invoice.jobId) return doc(db, "jobs", invoice.jobId);
    if (invoice.originalJobId) return doc(db, "jobs", invoice.originalJobId);
    if (invoice.wo) {
      const snap = await getDocs(query(collection(db, "jobs"), where("wo", "==", invoice.wo)));
      if (!snap.empty) return doc(db, "jobs", snap.docs[0].id);
    }
    return null;
  };

  const loadJobForInvoice = async (invoice) => {
    const jobRef = await findJobForInvoice(invoice);
    if (!jobRef) return null;
    const snap = await getDoc(jobRef);
    return snap.exists() ? { ref: jobRef, job: { id: snap.id, ...snap.data() } } : null;
  };

  /** The canonical visit date an invoice covers on a recurring job. */
  const invoiceOccurrenceKey = (invoice, job) => {
    const key = toDateKey(
      invoice.occurrenceKey ||
        invoice.originalOccurrenceDate ||
        invoice.occurrenceDate ||
        invoice.dateOfService ||
        invoice.serviceDate
    );
    if (!key) return "";
    return getCanonicalKeyForScheduledKey(job, key) || key;
  };

  /**
   * Move the job behind an invoice to "Invoice Sent". Recurring series are
   * never closed - only this visit's canonical date goes into invoicedDates.
   */
  const markJobInvoiceSent = async (invoice, stamp) => {
    try {
      const found = await loadJobForInvoice(invoice);
      if (!found) return;
      const { ref: jobRef, job } = found;
      const common = {
        invoiceSentAt: stamp,
        invoiceSentFromApprovedInvoiceId: invoice.id,
        updatedAt: new Date().toISOString(),
      };
      if (isRecurring(job)) {
        const key = invoiceOccurrenceKey(invoice, job);
        if (!key) return;
        await updateDoc(jobRef, { ...common, invoicedDates: arrayUnion(key), lastInvoiceSentAt: stamp });
      } else {
        await updateDoc(jobRef, { ...common, status: "Invoice Sent", invoiceStatus: "Sent" });
      }
    } catch (error) {
      console.error("Invoice saved as sent, but the job could not be updated:", error);
    }
  };

  /** Keep a one-time job's estimated total in step with its invoice. */
  const syncInvoiceAmountToJob = async (invoice) => {
    try {
      const found = await loadJobForInvoice(invoice);
      if (!found || isRecurring(found.job)) return; // recurring master total = the series default
      await updateDoc(found.ref, {
        estimatedTotal: numberValue(invoice.amount),
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.warn("Could not sync invoice amount to job:", error);
    }
  };

  const getInvoiceNumber = (invoice) =>
    invoice?.wo ? String(invoice.wo) : String(invoice?.invoiceNumber || invoice?.id || "");

  // Payments are applied with the SAME rule Deposits, Cash Plan and the Job
  // Estimate Spreadsheet use (utils/depositsInvoiceMatching): a payment that
  // names an invoice/visit pays that one; anything else on a WO pays its
  // invoices oldest visit first. So one check on a recurring contract no
  // longer marks every visit's invoice Paid.
  const getInvoicePaidTotal = (invoice, depositsList = deposits, invoiceList = invoices) => {
    if (!invoice?.id) return 0;
    const list = invoiceList.some((inv) => inv.id === invoice.id)
      ? invoiceList
      : [...invoiceList, invoice];
    return allocateDepositsToInvoices(list, depositsList)[invoice.id] || 0;
  };

  const isInvoiceFullyPaid = (invoice, depositsList = deposits) => {
    const amount = numberValue(invoice.amount);
    if (!amount) return statusOf(invoice) === "Paid";
    return getInvoicePaidTotal(invoice, depositsList) >= amount;
  };

  const splitActiveInvoices = (list, depositsList) => {
    const open = [];
    const sent = [];
    const paid = [];
    list.forEach((invoice) => {
      const status = statusOf(invoice);
      if (status === "Paid" || isInvoiceFullyPaid(invoice, depositsList)) paid.push(invoice);
      else if (status === "Sent") sent.push(invoice);
      else open.push(invoice);
    });
    return { open, sent, paid };
  };

  // ---------------------------------------------------------------- load
  useEffect(() => {
    const loadData = async () => {
      try {
        const [depositsSnap, propertiesSnap, managementSnap, invoicesSnap] = await Promise.all([
          getDocs(collection(db, "deposits")),
          getDocs(collection(db, "properties")),
          getDocs(collection(db, "managementCompanies")),
          getDocs(query(collection(db, "approvedInvoices"), orderBy("createdAt", "desc"))),
        ]);

        const depositsData = depositsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const propertiesData = propertiesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const managementData = managementSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        setDeposits(depositsData);
        setProperties(propertiesData);
        setManagementCompanies(managementData);

        const invoicesData = await Promise.all(
          invoicesSnap.docs.map(async (d) => {
            const invoice = { id: d.id, ...d.data() };
            const property = findPropertyForInvoice(invoice, propertiesData);
            let syncedAmount = invoice.amount;

            if (!invoice.amount && invoice.wo) {
              const jobsSnap = await getDocs(
                query(collection(db, "jobs"), where("wo", "==", invoice.wo))
              );
              if (!jobsSnap.empty) {
                syncedAmount = jobsSnap.docs[0].data().estimatedTotal || invoice.rate || 0;
              }
            }

            return {
              ...invoice,
              amount: syncedAmount,
              rate: invoice.rate || syncedAmount || 0,
              clientEmail:
                invoice.clientEmail || getAutoClientEmail(invoice, propertiesData, managementData),
              accountingEmail:
                invoice.accountingEmail || getAutoAccountingEmail(invoice, propertiesData, managementData),
              cityStateZip: getPropertyCityStateZip(property, invoice),
              management: getManagementName(invoice, property),
            };
          })
        );

        // Paid status is decided from the STORED invoice amounts (the same
        // values Deposits and the Job Estimate Spreadsheet use). The amount
        // filled in from the job above is for display only — using it here
        // made this page and Deposits flip the status back and forth.
        const storedInvoices = invoicesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const storedPaid = allocateDepositsToInvoices(storedInvoices, depositsData);
        const storedById = new Map(storedInvoices.map((inv) => [inv.id, inv]));
        const withPaidStatus = await Promise.all(
          invoicesData.map(async (invoice) => {
            const totalPaid = storedPaid[invoice.id] || 0;
            const stored = storedById.get(invoice.id) || invoice;
            if (
              sharedInvoiceTotal(stored) > 0 &&
              isInvoiceCovered(stored, totalPaid) &&
              statusOf(invoice) !== "Paid"
            ) {
              const paidAt = new Date().toISOString();
              await updateDoc(doc(db, "approvedInvoices", invoice.id), {
                status: "Paid",
                paid: true,
                paidAt,
                paidAmount: totalPaid,
                paymentMatchedFrom: "ApprovedInvoicesLoad",
                updatedAt: paidAt,
              });
              return { ...invoice, status: "Paid", paid: true, paidAt, paidAmount: totalPaid };
            }
            return { ...invoice, paidAmount: totalPaid };
          })
        );

        setInvoices(withPaidStatus);
      } catch (error) {
        console.error("Could not load approved invoices:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const replaceInvoiceLocally = (invoiceId, patch) => {
    setInvoices((prev) => prev.map((inv) => (inv.id === invoiceId ? { ...inv, ...patch } : inv)));
    setSelectedInvoice((prev) => (prev?.id === invoiceId ? { ...prev, ...patch } : prev));
  };

  // ---------------------------------------------------------------- sort / search
  const invoiceSortColumns = {
    siteName: "Site Name",
    invoiceNumber: "Invoice #",
    invoiceDate: "Invoice Date",
    serviceDate: "Service Date",
    status: "Status",
    amount: "Total",
  };

  const getInvoiceSortValue = (invoice, key) => {
    if (key === "siteName") return invoice.siteName || invoice.propertyName || "";
    if (key === "invoiceNumber") return invoice.wo || invoice.invoiceNumber || "";
    if (key === "invoiceDate") return invoice.invoiceDate || "";
    if (key === "serviceDate") return invoice.serviceDate || invoice.dateOfService || "";
    if (key === "status") return statusOf(invoice);
    if (key === "amount") return numberValue(invoice.amount);
    return invoice[key] || "";
  };

  const sortInvoices = (list = []) => {
    const { key, direction } = invoiceSort;
    const multiplier = direction === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const aValue = getInvoiceSortValue(a, key);
      const bValue = getInvoiceSortValue(b, key);
      if (key === "invoiceDate" || key === "serviceDate") {
        const aTime = aValue ? new Date(aValue).getTime() : 0;
        const bTime = bValue ? new Date(bValue).getTime() : 0;
        return (aTime - bTime) * multiplier;
      }
      if (key === "amount") return (aValue - bValue) * multiplier;
      if (key === "invoiceNumber") {
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        if (Number.isFinite(aNum) && Number.isFinite(bNum)) return (aNum - bNum) * multiplier;
      }
      return (
        String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: "base" }) *
        multiplier
      );
    });
  };

  const invoiceMatchesSearch = (invoice, searchTerm) => {
    const term = String(searchTerm || "").toLowerCase().trim();
    if (!term) return true;
    return [
      invoice.wo,
      invoice.invoiceNumber,
      invoice.invoiceNumber2,
      invoice.id,
      invoice.siteName,
      invoice.propertyName,
      invoice.scope,
      invoice.description,
      invoice.jobDescription,
      invoice.productService,
      invoice.customProductService,
      invoice.submittedBy,
      notesText(invoice.notes),
      ...(Array.isArray(invoice.serviceRows) ? invoice.serviceRows.map((r) => r.serviceDescription) : []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(term);
  };

  const getInvoiceDescriptionPreview = (invoice) => {
    const rowsText = hasRows(invoice)
      ? invoice.serviceRows.map((r) => r.serviceDescription).filter(Boolean).join(", ")
      : "";
    const description =
      rowsText ||
      invoice.description ||
      invoice.scope ||
      invoice.jobDescription ||
      invoice.productService ||
      invoice.customProductService ||
      "";
    const clean = String(description).replace(/\s+/g, " ").trim();
    return clean.length > 160 ? `${clean.slice(0, 160)}…` : clean || "-";
  };

  const toggleInvoiceSort = (key) =>
    setInvoiceSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));

  const renderSortableHeader = (key, label = invoiceSortColumns[key]) => (
    <th>
      <button type="button" className="ai-sort-header" onClick={() => toggleInvoiceSort(key)}>
        {label}
        {invoiceSort.key === key ? (invoiceSort.direction === "asc" ? " ▲" : " ▼") : ""}
      </button>
    </th>
  );

  // ---------------------------------------------------------------- continue
  const openContinuePopup = async (invoice) => {
    if (!invoice) return;
    setContinueInvoice(invoice);
    setContinueSourceJob(null);
    setContinueNextWO("");
    setContinueError("");
    setContinueLoading(true);
    try {
      const job = await findJobForWO({
        jobId: invoice.jobId,
        originalJobId: invoice.originalJobId,
        wo: invoice.wo,
      });
      if (!job) {
        setContinueError(
          `No job was found for work order ${invoice.wo || "(none on this invoice)"}. Continue needs the original job to copy from.`
        );
        return;
      }
      setContinueSourceJob(job);
      setContinueNextWO(await getNextContinuedWO(job.wo || invoice.wo));
    } catch (err) {
      console.error("Could not prepare the continuation:", err);
      setContinueError("Could not load the job behind this invoice.");
    } finally {
      setContinueLoading(false);
    }
  };

  const closeContinuePopup = () => {
    setContinueInvoice(null);
    setContinueSourceJob(null);
    setContinueNextWO("");
    setContinueError("");
  };

  const confirmContinue = async (newDate) => {
    if (!continueSourceJob?.id) return;
    setContinueSaving(true);
    setContinueError("");
    try {
      const created = await createContinuationJob({
        sourceJob: continueSourceJob,
        newDate,
        newWO: continueNextWO,
      });
      closeContinuePopup();
      alert(
        `New active job ${created.wo} created for ${newDate}.\n\n${continueSourceJob.wo || "The original job"} was not changed.`
      );
    } catch (err) {
      console.error("Could not continue this job:", err);
      setContinueError(err?.message || "Could not create the new job.");
    } finally {
      setContinueSaving(false);
    }
  };

  // ---------------------------------------------------------------- edit
  const openEdit = (invoice) => {
    const property = findPropertyForInvoice(invoice);
    setSelectedInvoice(invoice);
    setEditedInvoice({
      ...invoice,
      clientEmail: invoice.clientEmail || getAutoClientEmail(invoice, properties, managementCompanies),
      accountingEmail:
        invoice.accountingEmail || getAutoAccountingEmail(invoice, properties, managementCompanies),
      invoiceDate: invoice.invoiceDate || todayKey(),
      serviceDate: invoice.serviceDate || invoice.dateOfService || "",
      terms: invoice.terms || "Due Upon Invoice",
      productService: invoice.productService || "Pest Control Service",
      qty: invoice.qty || invoice.quantity || 1,
      rate: invoice.rate || invoice.amount || 0,
      amount: invoice.amount || invoice.rate || 0,
      cityStateZip: getPropertyCityStateZip(property, invoice),
      management: getManagementName(invoice, property),
      notes: notesText(invoice.notes),
      jobDescription: invoice.jobDescription || invoice.description || "",
      description: invoice.jobDescription || invoice.description || "",
      serviceRows: hasRows(invoice)
        ? invoice.serviceRows.map((row, index) => ({ ...row, _key: `row_${index}_${Date.now()}` }))
        : invoice.serviceRows,
      chemicalProducts: Array.isArray(invoice.chemicalProducts)
        ? invoice.chemicalProducts.map((p) => ({ ...p }))
        : [],
    });
    setIsEditing(true);
  };

  const closeDetails = () => {
    setSelectedInvoice(null);
    setEditedInvoice(null);
    setIsEditing(false);
  };

  const updateLineMath = (invoice) => {
    const qty = numberValue(invoice.qty);
    const rate = numberValue(invoice.rate);
    const amount = numberValue(invoice.amount);
    if (invoice._lastChanged === "amount") return { ...invoice, rate: qty ? amount / qty : amount };
    return { ...invoice, amount: qty * rate };
  };

  const handleEditChange = (field, value) => {
    setEditedInvoice((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "qty" || field === "rate") {
        next._lastChanged = "rate";
        return updateLineMath(next);
      }
      if (field === "amount") {
        next._lastChanged = "amount";
        return updateLineMath(next);
      }
      return next;
    });
  };

  const applyRowsTotal = (invoice, rows) => {
    const total = rowsTotal(rows);
    return {
      ...invoice,
      serviceRows: rows,
      amount: total,
      rate: total,
      price: total,
      quantity: rows.length === 1 ? String(rows[0].quantity || "1.00") : String(rows.length),
    };
  };

  const updateServiceRow = (index, field, value) =>
    setEditedInvoice((prev) =>
      applyRowsTotal(
        prev,
        prev.serviceRows.map((row, i) => (i === index ? { ...row, [field]: value } : row))
      )
    );

  const addServiceRow = () =>
    setEditedInvoice((prev) =>
      applyRowsTotal(prev, [
        ...(prev.serviceRows || []),
        { _key: `row_${Date.now()}`, serviceDescription: "", quantity: "1.00", amount: "" },
      ])
    );

  const removeServiceRow = (index) =>
    setEditedInvoice((prev) =>
      prev.serviceRows.length <= 1
        ? prev
        : applyRowsTotal(prev, prev.serviceRows.filter((_, i) => i !== index))
    );

  const convertToLineItems = () =>
    setEditedInvoice((prev) =>
      applyRowsTotal(prev, [
        {
          _key: `row_${Date.now()}`,
          serviceDescription:
            prev.serviceDescription || prev.serviceType || prev.productService || "Service",
          quantity: String(prev.qty || 1),
          amount: numberValue(prev.rate || prev.amount),
        },
      ])
    );

  const updateChemicals = (products) =>
    setEditedInvoice((prev) => ({
      ...prev,
      chemicalProducts: products,
      chemicalUsed: chemicalText(products),
      dropdownSelections: {
        ...(prev.dropdownSelections || {}),
        chemicalUsed: products.map((p) => p.chemical).filter(Boolean),
      },
    }));

  const uploadInvoiceImages = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !editedInvoice?.id) return;
    setUploadingImages(true);
    try {
      const storage = getStorage();
      const uploaded = [];
      for (const file of files) {
        const safeName = file.name.replace(/[^\w.-]+/g, "_");
        // images/** is allowed by Priority's storage.rules.
        const path = `images/approvedInvoices/${editedInvoice.id}/${Date.now()}_${safeName}`;
        const fileRef = ref(storage, path);
        await uploadBytes(fileRef, file);
        uploaded.push({ url: await getDownloadURL(fileRef), path, name: file.name });
      }
      setEditedInvoice((prev) => ({
        ...prev,
        images: [...(Array.isArray(prev.images) ? prev.images : []), ...uploaded],
      }));
    } catch (error) {
      console.error("Image upload failed:", error);
      alert("Could not upload one or more images.");
    } finally {
      setUploadingImages(false);
    }
  };

  const removeInvoiceImage = (index) => {
    if (!window.confirm("Remove this photo from the invoice? (The file itself is kept.)")) return;
    setEditedInvoice((prev) => ({
      ...prev,
      images: (Array.isArray(prev.images) ? prev.images : []).filter((_, i) => i !== index),
    }));
  };

  const saveInvoice = async () => {
    if (!editedInvoice?.id) return;
    setSavingInvoice(true);
    try {
      let payload = { ...editedInvoice };
      delete payload.id;
      delete payload._lastChanged;

      if (hasRows(payload)) {
        const rows = cleanRows(payload.serviceRows);
        const total = rows.reduce((sum, row) => sum + row.total, 0);
        payload = {
          ...payload,
          serviceRows: rows,
          amount: total,
          rate: total,
          price: total,
          quantity: rows.length === 1 ? rows[0].quantity : String(rows.length),
        };
      } else {
        payload.amount = numberValue(payload.amount);
        payload.rate = numberValue(payload.rate);
        payload.price = payload.amount;
      }

      payload.chemicalProducts = (payload.chemicalProducts || [])
        .filter((p) => String(p?.chemical || "").trim())
        .map((p) => ({ chemical: String(p.chemical).trim(), qty: String(p.qty || "").trim() }));
      payload.dateOfService = payload.serviceDate || payload.dateOfService || "";
      payload.description = payload.jobDescription || "";

      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) delete payload[key];
      });

      await updateDoc(doc(db, "approvedInvoices", editedInvoice.id), {
        ...payload,
        updatedAt: new Date().toISOString(),
      });

      const saved = { ...payload, id: editedInvoice.id };
      await syncInvoiceAmountToJob(saved);

      setInvoices((prev) => prev.map((inv) => (inv.id === saved.id ? saved : inv)));
      setSelectedInvoice(saved);
      setEditedInvoice(null);
      setIsEditing(false);
    } catch (error) {
      console.error("Could not save invoice:", error);
      alert("Could not save this invoice.");
    } finally {
      setSavingInvoice(false);
    }
  };

  const deleteInvoice = async (invoice) => {
    if (!invoice?.id) return;
    if (!window.confirm("Are you sure you want to delete this approved invoice?")) return;
    try {
      await deleteDoc(doc(db, "approvedInvoices", invoice.id));
      // Let Send Invoices offer "Create Invoice" for the job again.
      try {
        const found = await loadJobForInvoice(invoice);
        if (found && found.job.approvedInvoiceId === invoice.id) {
          await updateDoc(found.ref, { approvedInvoiceId: "", invoiceStatus: "", approvedInvoice: false });
        }
      } catch (jobErr) {
        console.warn("Invoice deleted, but its job link was not cleared:", jobErr);
      }
      setInvoices((prev) => prev.filter((inv) => inv.id !== invoice.id));
      closeDetails();
    } catch (error) {
      console.error("Could not delete invoice:", error);
      alert("Could not delete this invoice.");
    }
  };

  const setInvoiceStatus = async (invoice, status) => {
    try {
      const patch = { status, updatedAt: new Date().toISOString() };
      await updateDoc(doc(db, "approvedInvoices", invoice.id), patch);
      replaceInvoiceLocally(invoice.id, patch);
      try {
        const found = await loadJobForInvoice(invoice);
        if (found && found.job.approvedInvoiceId === invoice.id) {
          await updateDoc(found.ref, { invoiceStatus: status });
        }
      } catch (jobErr) {
        console.warn("Could not mirror invoice status on job:", jobErr);
      }
    } catch (error) {
      console.error("Could not update invoice status:", error);
      alert("Could not update the invoice status.");
    }
  };

  // ---------------------------------------------------------------- mark sent (not by email)
  const openMarkSentPopup = (invoice) => {
    setMarkSentInvoice(invoice);
    setMarkSentDate(localDayKey(new Date()));
    setMarkSentMethod("Mailed");
    setMarkSentNote("");
  };

  const saveMarkSent = async () => {
    if (!markSentInvoice || !markSentDate) return;
    setMarkSentSaving(true);
    try {
      /* Midday on the chosen local day so a timezone shift can't tip it into the day before. */
      const [y, m, d] = markSentDate.split("-").map(Number);
      const stamp = new Date(y, m - 1, d, 12, 0, 0).toISOString();
      const patch = {
        status: "Sent",
        sent: true,
        sentAt: stamp,
        sentMethod: markSentMethod,
        sentNote: markSentNote.trim() || null,
        updatedAt: new Date().toISOString(),
      };
      await updateDoc(doc(db, "approvedInvoices", markSentInvoice.id), patch);
      await markJobInvoiceSent(markSentInvoice, stamp);
      replaceInvoiceLocally(markSentInvoice.id, patch);
      setMarkSentInvoice(null);
    } catch (error) {
      console.error(error);
      alert(error.message || "Could not record that invoice as sent.");
    } finally {
      setMarkSentSaving(false);
    }
  };

  // ---------------------------------------------------------------- email
  const collectJobImages = (job = {}) => {
    const images = [];
    const addImage = (item, fallbackName = "Job Image") => {
      const url = imageUrlOf(item);
      if (!url) return;
      images.push({
        url,
        name: (typeof item === "object" && (item.name || item.fileName || item.filename)) || fallbackName,
      });
    };

    ["imageURLs", "fileURLs", "fileUpload", "fileUploads", "uploads", "images", "files"].forEach((field) => {
      const value = job[field];
      if (Array.isArray(value)) value.forEach((item, index) => addImage(item, `${field} ${index + 1}`));
      else addImage(value, field);
    });

    if (Array.isArray(job.notes)) {
      job.notes.forEach((note, noteIndex) => {
        ["files", "attachments", "images", "uploads"].forEach((field) => {
          if (Array.isArray(note?.[field])) {
            note[field].forEach((item, imgIndex) =>
              addImage(item, `Note ${noteIndex + 1} Image ${imgIndex + 1}`)
            );
          }
        });
      });
    }

    return images.filter(
      (img) => /\.(jpe?g|png|gif|webp|heic)(\?|$)/i.test(img.url) || /image/i.test(img.url) || /alt=media/.test(img.url)
    );
  };

  /** Invoice photos (pre-selected) + any other photos on the job (opt-in). */
  const loadImagesForInvoice = async (invoice) => {
    setLoadingEmailImages(true);
    const invoiceImages = normalizeImages(invoice.images).map((img) => ({ ...img, source: "invoice" }));
    let jobImages = [];
    try {
      const found = await loadJobForInvoice(invoice);
      if (found) jobImages = collectJobImages(found.job).map((img) => ({ ...img, source: "job" }));
    } catch (err) {
      console.warn("Could not load job images:", err);
    }

    const all = [...invoiceImages, ...jobImages].filter(
      (img, index, self) => img.url && self.findIndex((x) => x.url === img.url) === index
    );

    setAvailableInvoiceImages(all);
    setSelectedInvoiceImages(all.filter((img) => img.source === "invoice"));
    setLoadingEmailImages(false);
  };

  const openInvoiceEmailPopup = (invoice) => {
    // Priority pricing check: older invoices that carry both an initial fee and
    // a recurring fee - and were never told which one they bill - must pick
    // one first. Invoices from the completion form already chose.
    const initial = numberValue(invoice.initialAmount);
    const recurring = numberValue(invoice.recurringInvoiceAmount ?? invoice.amount);
    if (
      !invoice.invoicePricingSelection &&
      !invoice.skipRecurringPriceVerification &&
      initial > 0 &&
      recurring > 0
    ) {
      setPricingInvoice(invoice);
      return;
    }
    openInvoiceEmailPopupNow(invoice);
  };

  const selectInvoicePricing = async (selection) => {
    if (!pricingInvoice) return;
    try {
      const amount =
        selection === "initial"
          ? numberValue(pricingInvoice.initialAmount)
          : numberValue(pricingInvoice.recurringInvoiceAmount ?? pricingInvoice.amount);
      const updates = {
        amount,
        price: amount,
        rate: amount,
        invoicePricingSelection: selection,
        initialAmountInvoiced: selection === "initial" ? true : Boolean(pricingInvoice.initialAmountInvoiced),
        skipRecurringPriceVerification: selection === "recurring",
      };
      if (hasRows(pricingInvoice) && pricingInvoice.serviceRows.length === 1) {
        const row = pricingInvoice.serviceRows[0];
        updates.serviceRows = [{ ...row, amount, total: numberValue(row.quantity || 1) * amount }];
        updates.amount = updates.serviceRows[0].total;
        updates.price = updates.amount;
        updates.rate = updates.amount;
      }
      await updateDoc(doc(db, "approvedInvoices", pricingInvoice.id), updates);
      const found = await loadJobForInvoice(pricingInvoice);
      if (found && !isRecurring(found.job)) {
        await updateDoc(found.ref, {
          invoicePricingSelection: selection,
          initialAmountInvoiced: updates.initialAmountInvoiced,
          estimatedTotal: updates.amount,
          updatedAt: new Date().toISOString(),
        });
      } else if (found && selection === "initial") {
        await updateDoc(found.ref, { initialAmountInvoiced: true, updatedAt: new Date().toISOString() });
      }
      const next = { ...pricingInvoice, ...updates };
      replaceInvoiceLocally(next.id, updates);
      setPricingInvoice(null);
      openInvoiceEmailPopupNow(next);
    } catch (error) {
      console.error("Could not save pricing selection:", error);
      alert("Could not save the pricing selection.");
    }
  };

  const openInvoiceEmailPopupNow = (invoice) => {
    const accountingEmail =
      invoice.accountingEmail || getAutoAccountingEmail(invoice, properties, managementCompanies);
    setSelectedInvoiceForEmail(invoice);
    setInvoiceEmailData({
      accountingEmail,
      cc: "",
      bcc: "",
      subject: `Invoice #${invoice.wo || invoice.invoiceNumber || ""} - ${
        invoice.siteName || invoice.propertyName || "Priority Pest Defense"
      }`,
      message: "Please see the attached invoice from Priority Pest Defense. Thank you.",
      includePayLink: true,
    });
    setAvailableInvoiceImages([]);
    setSelectedInvoiceImages([]);
    loadImagesForInvoice(invoice);
  };

  const closeEmailPopup = () => {
    if (sendingInvoiceId) return;
    setSelectedInvoiceForEmail(null);
  };

  const sendInvoiceEmail = async () => {
    const invoice = selectedInvoiceForEmail;
    if (!invoice?.id) return;
    if (!invoiceEmailData.accountingEmail) {
      alert("Please enter an accounting email before sending.");
      return;
    }

    try {
      setSendingInvoiceId(invoice.id);
      const sendApprovedInvoiceEmail = httpsCallable(functions, "sendApprovedInvoiceEmail");
      const selectedImageUrls = selectedInvoiceImages.map(imageUrlOf).filter(Boolean);

      await sendApprovedInvoiceEmail({
        invoiceId: invoice.id,
        accountingEmail: invoiceEmailData.accountingEmail,
        cc: invoiceEmailData.cc,
        bcc: invoiceEmailData.bcc,
        subject: invoiceEmailData.subject,
        message: invoiceEmailData.message,
        selectedImageUrls,
        includePayLink: !!invoiceEmailData.includePayLink,
      });

      const stamp = new Date().toISOString();
      const patch = {
        accountingEmail: invoiceEmailData.accountingEmail,
        status: "Sent",
        sent: true,
        emailedAt: stamp,
        /* sentAt is shared by email and non-email sends so the totals see both. */
        sentAt: stamp,
        sentMethod: "Email",
        emailedImageUrls: selectedImageUrls,
        updatedAt: stamp,
        ...(invoiceEmailData.includePayLink ? { payLinkSent: true, payLinkSentAt: stamp } : {}),
      };
      await updateDoc(doc(db, "approvedInvoices", invoice.id), patch);
      await markJobInvoiceSent(invoice, stamp);

      replaceInvoiceLocally(invoice.id, patch);
      alert("Invoice email sent successfully.");
      setSelectedInvoiceForEmail(null);
    } catch (error) {
      console.error(error);
      alert(error.message || "Failed to send invoice email.");
    } finally {
      setSendingInvoiceId(null);
    }
  };

  // ---------------------------------------------------------------- paid
  const openInvoicePaidPopup = (invoice) => {
    const samePropertyInvoices = invoices
      .filter((inv) => {
        const sameProperty =
          (inv.siteName || inv.propertyName || "") === (invoice.siteName || invoice.propertyName || "");
        return sameProperty && statusOf(inv) !== "Paid" && !isInvoiceFullyPaid(inv);
      })
      .map((inv) => ({
        selected: inv.id === invoice.id,
        invoiceId: inv.id,
        wo: getInvoiceNumber(inv),
        siteName: inv.siteName || inv.propertyName || "",
        scope: inv.description || inv.scope || "",
        amount: inv.id === invoice.id ? numberValue(inv.amount) : "",
        invoiceTotal: numberValue(inv.amount),
      }));

    setInvoiceBeingPaid(invoice);
    setInvoicePaymentMode("single");
    setPaidVendor("PPD");
    setPaidVendorOther("");
    setPaidCheckNumber("");
    setPaidCheckDate(todayKey());
    setPaidReference("");
    setPaidCheckAmount(numberValue(invoice.amount) || "");
    setSplitPaidRows(samePropertyInvoices);
  };

  const closePaidPopup = () => {
    if (savingPayment) return;
    setInvoiceBeingPaid(null);
  };

  const syncJobTotalFromDeposits = async (invoiceNumber) => {
    if (!invoiceNumber) return;
    const depSnap = await getDocs(collection(db, "deposits"));
    let total = 0;
    let lastDeposit = null;
    depSnap.docs.forEach((d) => {
      const data = d.data();
      if (String(data.invoiceNumber || "") === String(invoiceNumber)) {
        total += numberValue(data.amount);
        lastDeposit = data;
      }
    });
    // Payments received go to job_payment_totals (same as Deposits).
    // job_totals.total is the job's PRICE and must not be overwritten here.
    await setDoc(
      doc(db, "job_payment_totals", String(invoiceNumber)),
      {
        wo: String(invoiceNumber),
        paidTotal: total,
        company: lastDeposit?.company || "",
        description: lastDeposit?.description || "",
        lastCheckNumber: lastDeposit?.checkNumber || "",
        invoiceNumber2: lastDeposit?.invoiceNumber2 || "",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const saveInvoicePayment = async () => {
    if (!invoiceBeingPaid?.id) return;
    if (!paidCheckNumber || !paidCheckDate || !paidCheckAmount) {
      alert("Please enter check number, check date, and check amount.");
      return;
    }
    const vendorValue = paidVendor === "Other" ? paidVendorOther : paidVendor;
    if (!vendorValue) {
      alert("Please select or enter a vendor.");
      return;
    }

    setSavingPayment(true);
    try {
      const createdDeposits = [];

      if (invoicePaymentMode === "single") {
        const invoiceNumber = getInvoiceNumber(invoiceBeingPaid);
        const depositData = {
          property: invoiceBeingPaid.siteName || invoiceBeingPaid.propertyName || "",
          amount: numberValue(paidCheckAmount),
          checkNumber: paidCheckNumber || "",
          invoiceNumber,
          invoiceNumber2: paidReference || "",
          // Tie the payment to THIS invoice / visit so recurring contracts
          // don't mark every visit paid.
          approvedInvoiceId: invoiceBeingPaid.id,
          jobId: invoiceBeingPaid.originalJobId || invoiceBeingPaid.jobId || "",
          occurrenceKey: toDateKey(invoiceBeingPaid.occurrenceKey) || "",
          description: invoiceBeingPaid.description || invoiceBeingPaid.scope || "",
          company: vendorValue,
          date: paidCheckDate || "",
          createdFrom: "ApprovedInvoicesPaidButton",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        const docRef = await addDoc(collection(db, "deposits"), depositData);
        createdDeposits.push({ id: docRef.id, ...depositData });
        await syncJobTotalFromDeposits(invoiceNumber);
      }

      if (invoicePaymentMode === "split") {
        const selectedRows = splitPaidRows.filter((row) => row.selected && numberValue(row.amount) > 0);
        if (!selectedRows.length) {
          alert("Select at least one invoice to split this check.");
          return;
        }
        const enteredTotal = selectedRows.reduce((sum, row) => sum + numberValue(row.amount), 0);
        if (numberValue(paidCheckAmount) !== enteredTotal) {
          const proceed = window.confirm(
            `The split amounts total ${formatMoney(enteredTotal)}, but the check amount is ${formatMoney(
              paidCheckAmount
            )}. Do you want to continue?`
          );
          if (!proceed) return;
        }
        const checkGroupId = `${paidCheckNumber}_${paidCheckDate}_${
          invoiceBeingPaid.siteName || invoiceBeingPaid.propertyName || ""
        }`
          .replace(/\s+/g, "_")
          .replace(/[^a-zA-Z0-9_-]/g, "");

        for (const row of selectedRows) {
          const depositData = {
            property: row.siteName || invoiceBeingPaid.siteName || "",
            amount: numberValue(row.amount),
            checkNumber: paidCheckNumber || "",
            invoiceNumber: row.wo ? String(row.wo) : "",
            invoiceNumber2: selectedRows.map((r) => r.wo).join(", "),
            approvedInvoiceId: row.invoiceId || "",
            jobId:
              (invoices.find((inv) => inv.id === row.invoiceId) || {}).originalJobId ||
              (invoices.find((inv) => inv.id === row.invoiceId) || {}).jobId ||
              "",
            occurrenceKey:
              toDateKey((invoices.find((inv) => inv.id === row.invoiceId) || {}).occurrenceKey) || "",
            description: row.scope || invoiceBeingPaid.description || invoiceBeingPaid.scope || "",
            company: vendorValue,
            date: paidCheckDate || "",
            checkGroupId,
            originalCheckAmount: numberValue(paidCheckAmount),
            splitCheck: true,
            createdFrom: "ApprovedInvoicesSplitPaidButton",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          const docRef = await addDoc(collection(db, "deposits"), depositData);
          createdDeposits.push({ id: docRef.id, ...depositData });
          await syncJobTotalFromDeposits(row.wo);
        }
      }

      const updatedDeposits = [...deposits, ...createdDeposits];
      setDeposits(updatedDeposits);

      const affectedInvoiceNumbers = new Set(createdDeposits.map((d) => String(d.invoiceNumber || "")));
      const affectedInvoiceIds = new Set();
      if (invoicePaymentMode === "single") affectedInvoiceIds.add(invoiceBeingPaid.id);
      if (invoicePaymentMode === "split") {
        splitPaidRows
          .filter((row) => row.selected && numberValue(row.amount) > 0)
          .forEach((row) => row.invoiceId && affectedInvoiceIds.add(row.invoiceId));
      }

      const paidAt = new Date().toISOString();
      // The invoice(s) the Paid button was pressed for are Paid. Other
      // invoices on the same WO only turn Paid if the payments now cover them.
      const paidMap = allocateDepositsToInvoices(invoices, updatedDeposits);
      const paidUpdates = {};
      invoices.forEach((inv) => {
        const clicked = affectedInvoiceIds.has(inv.id);
        const sameWO = affectedInvoiceNumbers.has(getInvoiceNumber(inv));
        if (!clicked && !sameWO) return;
        const paidAmount = paidMap[inv.id] || 0;
        const covered = sharedInvoiceTotal(inv) > 0 && isInvoiceCovered(inv, paidAmount);
        if (!clicked && (!covered || statusOf(inv) === "Paid")) return;
        paidUpdates[inv.id] = {
          status: "Paid",
          paid: true,
          paidAt,
          paidAmount,
          paymentMatchedFrom: clicked ? "ApprovedInvoicesPaidButton" : "deposits",
          updatedAt: paidAt,
        };
      });

      for (const [invoiceId, patch] of Object.entries(paidUpdates)) {
        await updateDoc(doc(db, "approvedInvoices", invoiceId), patch);
      }

      setInvoices((prev) =>
        prev.map((inv) => (paidUpdates[inv.id] ? { ...inv, ...paidUpdates[inv.id] } : inv))
      );

      setInvoiceBeingPaid(null);
      setSplitPaidRows([]);
      alert("Invoice payment saved.");
    } catch (error) {
      console.error("Could not save payment:", error);
      alert("Could not save the payment.");
    } finally {
      setSavingPayment(false);
    }
  };

  // ---------------------------------------------------------------- print
  const printInvoice = () => {
    const printable = document.getElementById("approvedInvoicePrintable");
    if (!printable) return;

    const iframe = document.createElement("iframe");
    Object.assign(iframe.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
    document.body.appendChild(iframe);

    const iDoc = iframe.contentWindow.document;
    iDoc.open();
    iDoc.write(`
      <!doctype html>
      <html>
        <head>
          <title>Invoice</title>
          <style>
            @page { size: Letter; margin: 0.5in; }
            body { font-family: Arial, sans-serif; color: #111; font-size: 12px; }
            .ai-print-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1d4ed8; padding-bottom: 14px; margin-bottom: 18px; }
            .ai-print-logo { width: 180px; max-height: 120px; object-fit: contain; display: block; margin-bottom: 6px; }
            .ai-print-title { font-size: 30px; font-weight: 800; text-align: right; }
            .ai-print-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 16px; line-height: 1.45; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th { background: #111827; color: white; padding: 7px; text-align: left; }
            td { border-bottom: 1px solid #d1d5db; padding: 7px; vertical-align: top; }
            .ai-print-box { margin-top: 14px; border: 1px solid #d1d5db; padding: 10px 12px; }
            .ai-print-box h4 { margin: 0 0 6px; }
            .ai-print-pre { white-space: pre-line; }
            .ai-print-photos { display: flex; flex-wrap: wrap; gap: 8px; }
            .ai-print-photos img { width: 150px; height: 112px; object-fit: cover; border: 1px solid #ccc; }
            .ai-print-sigs { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 14px; }
            .ai-print-sigs img { width: 100%; max-width: 280px; display: block; margin-top: 6px; }
            .ai-print-total { text-align: right; margin-top: 18px; font-size: 18px; font-weight: bold; }
            .no-print { display: none !important; }
          </style>
        </head>
        <body>${printable.outerHTML}</body>
      </html>
    `);
    iDoc.close();

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 300);
  };

  // ---------------------------------------------------------------- derived
  const [rangeFrom, rangeTo] = useMemo(() => {
    const today = localDayKey(new Date());
    if (totalsRange === "today") return [today, today];
    if (totalsRange === "week") return [startOfWeekKey(), today];
    if (totalsRange === "last7") return [daysAgoKey(6), today];
    if (totalsRange === "month") return [startOfMonthKey(), today];
    if (totalsRange === "last30") return [daysAgoKey(29), today];
    const a = totalsFrom || today;
    const b = totalsTo || today;
    return a <= b ? [a, b] : [b, a];
  }, [totalsRange, totalsFrom, totalsTo]);

  const sentStats = useMemo(() => {
    const byDay = new Map();
    let count = 0;
    let amount = 0;
    let undated = 0;

    invoices.forEach((invoice) => {
      const day = localDayKey(sentTimestamp(invoice));
      if (!day) {
        if (statusOf(invoice) === "Sent") undated += 1;
        return;
      }
      if (day < rangeFrom || day > rangeTo) return;
      const value = numberValue(invoice.amount);
      count += 1;
      amount += value;
      const row = byDay.get(day) || { day, count: 0, amount: 0 };
      row.count += 1;
      row.amount += value;
      byDay.set(day, row);
    });

    const days = Array.from(byDay.values()).sort((a, b) => b.day.localeCompare(a.day));
    return { count, amount, undated, days, average: days.length ? amount / days.length : 0 };
  }, [invoices, rangeFrom, rangeTo]);

  const { open: openInvoices, sent: sentInvoices, paid: paidInvoices } = useMemo(
    () => splitActiveInvoices(invoices, deposits),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invoices, deposits]
  );

  const sortedOpenInvoices = useMemo(
    () => sortInvoices(openInvoices.filter((inv) => invoiceMatchesSearch(inv, approvedInvoiceSearch))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openInvoices, approvedInvoiceSearch, invoiceSort]
  );
  const sortedSentInvoices = useMemo(
    () => sortInvoices(sentInvoices.filter((inv) => invoiceMatchesSearch(inv, sentInvoiceSearch))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sentInvoices, sentInvoiceSearch, invoiceSort]
  );
  const sortedPaidInvoices = useMemo(
    () => sortInvoices(paidInvoices.filter((inv) => invoiceMatchesSearch(inv, paidInvoiceSearch))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paidInvoices, paidInvoiceSearch, invoiceSort]
  );

  const openTotal = openInvoices.reduce((sum, inv) => sum + numberValue(inv.amount), 0);
  const draftCount = openInvoices.filter((inv) => statusOf(inv) === "Draft").length;

  const activeInvoice = !isEditing ? selectedInvoice : null;

  // ---------------------------------------------------------------- render helpers
  const renderTable = (list, { kind, search, empty }) => (
    <div className="ai-table-wrap">
      <table className="ai-table">
        <thead>
          <tr>
            {renderSortableHeader("siteName")}
            {renderSortableHeader("invoiceNumber")}
            <th>Description</th>
            {renderSortableHeader("invoiceDate")}
            {renderSortableHeader("serviceDate", "Service Date")}
            <th>Terms</th>
            {renderSortableHeader("status")}
            {renderSortableHeader("amount", "Total")}
            {kind === "paid" && <th>Paid</th>}
            {kind === "sent" && <th>Sent</th>}
            <th className="ai-col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="10" className="ai-empty">Loading invoices…</td>
            </tr>
          ) : list.length === 0 ? (
            <tr>
              <td colSpan="10" className="ai-empty">{search ? "No invoices match your search." : empty}</td>
            </tr>
          ) : (
            list.map((invoice) => {
              const photoCount = normalizeImages(invoice.images).length;
              const notes = notesText(invoice.notes);
              return (
                <tr key={invoice.id} className={`ai-row ai-row--${kind}`}>
                  <td className="ai-strong">{invoice.siteName || invoice.propertyName}</td>
                  <td>{invoice.wo || invoice.invoiceNumber || ""}</td>
                  <td className="ai-desc-cell">
                    {getInvoiceDescriptionPreview(invoice)}
                    <div className="ai-row-meta">
                      {photoCount > 0 && <span className="ai-chip">{photoCount} photo{photoCount === 1 ? "" : "s"}</span>}
                      {notes && <span className="ai-chip ai-chip--note">Has notes</span>}
                      {invoice.submittedBy && <span className="ai-chip">By {invoice.submittedBy}</span>}
                    </div>
                  </td>
                  <td>{invoice.invoiceDate}</td>
                  <td>{invoice.serviceDate || invoice.dateOfService}</td>
                  <td>{invoice.terms}</td>
                  <td>
                    <StatusPill status={statusOf(invoice)} />
                  </td>
                  <td className="ai-strong">{formatMoney(invoice.amount)}</td>
                  {kind === "paid" && <td>{formatMoney(getInvoicePaidTotal(invoice))}</td>}
                  {kind === "sent" && (
                    <td className="ai-small">
                      {localDayKey(sentTimestamp(invoice)) ? prettyDay(localDayKey(sentTimestamp(invoice))) : "—"}
                      {invoice.sentMethod ? <div className="ai-muted">{invoice.sentMethod}</div> : null}
                    </td>
                  )}
                  <td className="ai-col-actions">
                    <div className="ai-action-stack">
                      <button type="button" className="ai-btn ai-btn--blue ai-btn--sm" onClick={() => setSelectedInvoice(invoice)}>
                        Review
                      </button>
                      {kind !== "paid" && (
                        <>
                          <button type="button" className="ai-btn ai-btn--outline ai-btn--sm" onClick={() => openEdit(invoice)}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="ai-btn ai-btn--green ai-btn--sm"
                            onClick={() => openInvoiceEmailPopup(invoice)}
                            disabled={sendingInvoiceId === invoice.id}
                          >
                            {sendingInvoiceId === invoice.id ? "Sending..." : kind === "sent" ? "Resend" : "Send Invoice"}
                          </button>
                          {kind === "open" && (
                            <button
                              type="button"
                              className="ai-btn ai-btn--grey ai-btn--sm"
                              onClick={() => openMarkSentPopup(invoice)}
                              title="Record this invoice as sent by mail, hand or portal"
                            >
                              Mark as Sent
                            </button>
                          )}
                          <button type="button" className="ai-btn ai-btn--teal ai-btn--sm" onClick={() => openInvoicePaidPopup(invoice)}>
                            Invoice Paid
                          </button>
                          <button type="button" className="ai-btn ai-btn--red ai-btn--sm" onClick={() => deleteInvoice(invoice)}>
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  const renderPrintable = (invoice) => {
    const management = getManagementForInvoice(invoice);
    const managementName =
      invoice.management ||
      invoice.managementCompany ||
      management?.management ||
      management?.managementCompany ||
      management?.companyName ||
      management?.name ||
      "";
    const managementAddress =
      invoice.managementAddress || management?.managementAddress || management?.address || "";
    const managementCityStateZip =
      invoice.managementCityStateZip ||
      management?.managementCityStateZip ||
      [management?.city, management?.state, management?.zip].filter(Boolean).join(", ");
    const productServiceLabel =
      invoice.productService === "Other" ? invoice.customProductService : invoice.productService || "";
    const rows = printableRows(invoice);
    const images = normalizeImages(invoice.images);
    const notes = notesText(invoice.notes);
    const details = [
      ["Chemicals Used", invoice.chemicalUsed],
      ["Pest Treated", invoice.pestTreated],
      ["Areas Treated", invoice.areasTreated],
      ["Equipment Used", invoice.equipmentUsed],
      ["Weather", invoice.weather],
      ["Temperature", invoice.temperature],
      ["Wind Speed", invoice.windSpeed],
      ["Wind Direction", invoice.windDirection],
    ].filter(([, value]) => String(value || "").trim());
    const timeLine =
      invoice.timeIn || invoice.timeOut ? `${invoice.timeIn || ""} - ${invoice.timeOut || ""}` : "";

    return (
      <div id="approvedInvoicePrintable" className="ai-print">
        <div className="ai-print-header">
          <div>
            <img src={PPD_LOGO_URL} alt="Priority Pest Defense logo" className="ai-print-logo" />
            <strong>{COMPANY.name}</strong>
            <br />
            {COMPANY.addressLine1}
            <br />
            {COMPANY.addressLine2}
            <br />
            {COMPANY.cityStateZip}
            <br />
            {COMPANY.email}
            <br />
            {COMPANY.phone}
          </div>
          <div className="ai-print-title">
            INVOICE
            <br />
            <span style={{ fontSize: 14, fontWeight: 600 }}>Invoice #: {invoice.wo || invoice.invoiceNumber || ""}</span>
          </div>
        </div>

        <div className="ai-print-grid">
          <div>
            <strong>Bill To</strong>
            <br />
            {invoice.billTo || invoice.propertyName || invoice.siteName || ""}
            {managementName ? (
              <>
                <br />
                c/o {managementName}
                {managementAddress && (
                  <>
                    <br />
                    {managementAddress}
                  </>
                )}
                {managementCityStateZip && (
                  <>
                    <br />
                    {managementCityStateZip}
                  </>
                )}
              </>
            ) : (
              <>
                <br />
                {invoice.billingAddress || invoice.address || ""}
                <br />
                {invoice.cityStateZip || ""}
              </>
            )}
            <br />
            {invoice.accountingEmail || invoice.clientEmail || invoice.managerEmail || ""}
          </div>
          <div>
            <strong>Invoice Details</strong>
            <br />
            Invoice #: {invoice.wo || invoice.invoiceNumber || ""}
            <br />
            Terms: {invoice.terms || "Due Upon Invoice"}
            <br />
            Invoice Date: {invoice.invoiceDate || ""}
            <br />
            Service Date: {invoice.serviceDate || invoice.dateOfService || ""}
            <br />
            PO: {invoice.purchaseOrder || "None"}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Product/Service</th>
              <th>Description</th>
              <th>Qty</th>
              <th>Rate</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>{invoice.serviceDate || invoice.dateOfService || ""}</td>
                <td>{productServiceLabel}</td>
                <td className="ai-print-pre">{row.description}</td>
                <td>{row.quantity}</td>
                <td>{formatMoney(row.rate)}</td>
                <td>{formatMoney(row.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {(details.length > 0 || timeLine || (invoice.jobDescription && hasRows(invoice))) && (
          <div className="ai-print-box">
            <h4>Service Inspection Details</h4>
            {invoice.jobDescription && hasRows(invoice) && (
              <p>
                <strong>Service Notes:</strong>
                <br />
                <span className="ai-print-pre">{invoice.jobDescription}</span>
              </p>
            )}
            {details.map(([label, value]) => (
              <p key={label}>
                <strong>{label}:</strong>
                <br />
                <span className="ai-print-pre">{value}</span>
              </p>
            ))}
            {timeLine && (
              <p>
                <strong>Time:</strong> {timeLine}
              </p>
            )}
          </div>
        )}

        {notes && (
          <div className="ai-print-box">
            <h4>Notes</h4>
            <div className="ai-print-pre">{notes}</div>
          </div>
        )}

        {images.length > 0 && (
          <div className="ai-print-box">
            <h4>Photos</h4>
            <div className="ai-print-photos">
              {images.map((img) => (
                <img key={img.url} src={img.url} alt={img.name} />
              ))}
            </div>
          </div>
        )}

        {(invoice.technicianSignature || invoice.customerSignature || invoice.customerUnavailableToSign) && (
          <div className="ai-print-sigs">
            <div>
              <strong>Technician Signature</strong>
              {invoice.technicianSignature && <img src={invoice.technicianSignature} alt="Technician signature" />}
            </div>
            <div>
              <strong>Customer Signature</strong>
              {invoice.customerSignature ? (
                <img src={invoice.customerSignature} alt="Customer signature" />
              ) : invoice.customerUnavailableToSign ? (
                <p>Signature Not Necessary</p>
              ) : null}
            </div>
          </div>
        )}

        <div className="ai-print-total">Total: {formatMoney(invoice.amount || invoice.price)}</div>
      </div>
    );
  };

  const renderReviewPanel = (invoice) => {
    const checkIn = formatDateTime(invoice.checkInAt);
    const checkOut = formatDateTime(invoice.checkOutAt);
    return (
      <div className="ai-review no-print">
        <div className="ai-review-item">
          <span className="ai-review-label">Status</span>
          <StatusPill status={statusOf(invoice)} />
        </div>
        <div className="ai-review-item">
          <span className="ai-review-label">Total</span>
          <span className="ai-strong">{formatMoney(invoice.amount)}</span>
        </div>
        <div className="ai-review-item">
          <span className="ai-review-label">Pricing</span>
          <span>
            {invoice.invoicePricingSelection === "initial"
              ? "Initial fee"
              : invoice.invoicePricingSelection === "monthly" || invoice.invoicePricingSelection === "recurring"
              ? "Monthly / recurring fee"
              : "—"}
          </span>
        </div>
        <div className="ai-review-item">
          <span className="ai-review-label">Submitted</span>
          <span>
            {[invoice.submittedBy, invoice.submittedDate].filter(Boolean).join(" · ") || "—"}
          </span>
        </div>
        <div className="ai-review-item">
          <span className="ai-review-label">Time In</span>
          <span>
            {invoice.timeIn || "—"}
            {invoice.timeInSource && <span className="ai-muted"> ({sourceLabel(invoice.timeInSource)})</span>}
          </span>
        </div>
        <div className="ai-review-item">
          <span className="ai-review-label">Time Out</span>
          <span>
            {invoice.timeOut || "—"}
            {invoice.timeOutSource && <span className="ai-muted"> ({sourceLabel(invoice.timeOutSource)})</span>}
          </span>
        </div>
        {(checkIn || checkOut) && (
          <div className="ai-review-item">
            <span className="ai-review-label">Check-in / out</span>
            <span>{[checkIn, checkOut].filter(Boolean).join(" → ")}</span>
          </div>
        )}
        {invoice.temperature && (
          <div className="ai-review-item">
            <span className="ai-review-label">Temperature</span>
            <span>{invoice.temperature}</span>
          </div>
        )}
        <div className="ai-review-item">
          <span className="ai-review-label">Send to</span>
          <span>{invoice.accountingEmail || invoice.clientEmail || invoice.managerEmail || "No email on file"}</span>
        </div>
        {invoice.sentAt || invoice.emailedAt ? (
          <div className="ai-review-item">
            <span className="ai-review-label">Sent</span>
            <span>
              {formatDateTime(sentTimestamp(invoice))}
              {invoice.sentMethod ? ` · ${invoice.sentMethod}` : ""}
            </span>
          </div>
        ) : null}
      </div>
    );
  };

  const editRows = editedInvoice && hasRows(editedInvoice);
  const editImages = editedInvoice ? (Array.isArray(editedInvoice.images) ? editedInvoice.images : []) : [];

  return (
    <div className="ai-page">
      <header className="ai-header">
        <div>
          <h2 className="ai-title">Approved Invoices</h2>
          <p className="ai-subtitle">
            Review every invoice before it goes to the client. Edit the details, photos and notes, then
            send it - nothing is emailed until you press Send Invoice.
          </p>
        </div>
        <div className="ai-header-stats">
          <div className="ai-stat">
            <span className="ai-stat-label">Waiting for review</span>
            <span className="ai-stat-value">{openInvoices.length}</span>
          </div>
          <div className="ai-stat">
            <span className="ai-stat-label">Drafts</span>
            <span className="ai-stat-value ai-stat-value--amber">{draftCount}</span>
          </div>
          <div className="ai-stat">
            <span className="ai-stat-label">Open total</span>
            <span className="ai-stat-value ai-stat-value--green">{formatMoney(openTotal)}</span>
          </div>
        </div>
      </header>

      {/* ================= Sent totals ================= */}
      <section className="ai-totals">
        <div className="ai-totals-head">
          <h3>Invoices sent</h3>
          <div className="ai-presets">
            {RANGE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`ai-preset${totalsRange === preset.id ? " is-active" : ""}`}
                onClick={() => setTotalsRange(preset.id)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {totalsRange === "custom" && (
          <div className="ai-totals-custom">
            <label>
              From
              <input type="date" value={totalsFrom} onChange={(e) => setTotalsFrom(e.target.value)} />
            </label>
            <label>
              To
              <input type="date" value={totalsTo} onChange={(e) => setTotalsTo(e.target.value)} />
            </label>
          </div>
        )}

        <div className="ai-figures">
          <div className="ai-figure">
            <span className="ai-figure-label">Invoices sent</span>
            <span className="ai-figure-value">{sentStats.count}</span>
          </div>
          <div className="ai-figure">
            <span className="ai-figure-label">Total billed</span>
            <span className="ai-figure-value ai-figure-value--money">{formatMoney(sentStats.amount)}</span>
          </div>
          <div className="ai-figure">
            <span className="ai-figure-label">Avg per sending day</span>
            <span className="ai-figure-value ai-figure-value--money">{formatMoney(sentStats.average)}</span>
          </div>
        </div>

        <div className="ai-totals-foot">
          <span className="ai-muted">
            {rangeFrom === rangeTo ? prettyDay(rangeFrom) : `${prettyDay(rangeFrom)} – ${prettyDay(rangeTo)}`}
          </span>
          {sentStats.days.length > 0 && (
            <button type="button" className="ai-link" onClick={() => setShowDailyBreakdown((v) => !v)}>
              {showDailyBreakdown ? "Hide daily breakdown" : "Daily breakdown"}
            </button>
          )}
        </div>

        {showDailyBreakdown && sentStats.days.length > 0 && (
          <table className="ai-mini-table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Sent</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {sentStats.days.map((row) => (
                <tr key={row.day}>
                  <td>{prettyDay(row.day)}</td>
                  <td>{row.count}</td>
                  <td>{formatMoney(row.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {sentStats.count === 0 && <p className="ai-muted ai-small">No invoices sent in this range.</p>}
        {sentStats.undated > 0 && (
          <p className="ai-note">
            {sentStats.undated} invoice{sentStats.undated === 1 ? " is" : "s are"} marked Sent with no send date
            recorded, so {sentStats.undated === 1 ? "it is" : "they are"} not counted above. Use Mark as Sent to give{" "}
            {sentStats.undated === 1 ? "it" : "them"} a date.
          </p>
        )}
      </section>

      {/* ================= Open invoices ================= */}
      <section className="ai-card">
        <div className="ai-card-head">
          <h3 className="ai-card-title">
            Waiting for Review <span className="ai-count">{sortedOpenInvoices.length}</span>
          </h3>
          <div className="ai-search">
            <input
              type="text"
              value={approvedInvoiceSearch}
              onChange={(e) => setApprovedInvoiceSearch(e.target.value)}
              placeholder="Search by invoice #, site name, service, notes, or technician..."
            />
          </div>
        </div>
        {renderTable(sortedOpenInvoices, {
          kind: "open",
          search: approvedInvoiceSearch,
          empty: "No invoices waiting. Invoices created from a completed job show up here.",
        })}
      </section>

      {/* ================= Sent invoices ================= */}
      <section className="ai-card">
        <div className="ai-card-head">
          <h3 className="ai-card-title">
            Sent Invoices <span className="ai-count">{sentInvoices.length}</span>
          </h3>
          <div className="ai-card-tools">
            {showSentInvoices && (
              <div className="ai-search">
                <input
                  type="text"
                  value={sentInvoiceSearch}
                  onChange={(e) => setSentInvoiceSearch(e.target.value)}
                  placeholder="Search sent invoices..."
                />
              </div>
            )}
            <button type="button" className="ai-btn ai-btn--outline ai-btn--sm" onClick={() => setShowSentInvoices((v) => !v)}>
              {showSentInvoices ? "Hide Sent Invoices" : "Show Sent Invoices"}
            </button>
          </div>
        </div>
        {showSentInvoices &&
          renderTable(sortedSentInvoices, { kind: "sent", search: sentInvoiceSearch, empty: "No sent invoices yet." })}
      </section>

      {/* ================= Paid invoices ================= */}
      <section className="ai-card">
        <div className="ai-card-head">
          <h3 className="ai-card-title">
            Paid Invoices <span className="ai-count">{paidInvoices.length}</span>
          </h3>
          <div className="ai-card-tools">
            {showPaidInvoices && (
              <div className="ai-search">
                <input
                  type="text"
                  value={paidInvoiceSearch}
                  onChange={(e) => setPaidInvoiceSearch(e.target.value)}
                  placeholder="Search paid invoices..."
                />
              </div>
            )}
            <button type="button" className="ai-btn ai-btn--outline ai-btn--sm" onClick={() => setShowPaidInvoices((v) => !v)}>
              {showPaidInvoices ? "Hide Paid Invoices" : "Show Paid Invoices"}
            </button>
          </div>
        </div>
        {showPaidInvoices &&
          renderTable(sortedPaidInvoices, { kind: "paid", search: paidInvoiceSearch, empty: "No paid invoices yet." })}
      </section>

      {/* ================= Review (details) popup ================= */}
      {activeInvoice && (
        <AiPopup
          layer="main"
          width={1080}
          title="Review Invoice"
          subtitle={[
            `Invoice #${activeInvoice.wo || activeInvoice.invoiceNumber || ""}`,
            activeInvoice.siteName,
          ]
            .filter(Boolean)
            .join(" · ")}
          onClose={closeDetails}
        >
          {renderReviewPanel(activeInvoice)}
          {renderPrintable(activeInvoice)}

          <div className="ai-btn-row no-print">
            <button type="button" className="ai-btn ai-btn--blue" onClick={() => openEdit(activeInvoice)}>
              Edit
            </button>
            {statusOf(activeInvoice) !== "Sent" && statusOf(activeInvoice) !== "Paid" && (
              <>
                <button type="button" className="ai-btn ai-btn--green" onClick={() => setInvoiceStatus(activeInvoice, "Reviewed")}>
                  Mark Reviewed
                </button>
                <button type="button" className="ai-btn ai-btn--grey" onClick={() => setInvoiceStatus(activeInvoice, "Draft")}>
                  Save as Draft
                </button>
              </>
            )}
            <button type="button" className="ai-btn ai-btn--black" onClick={printInvoice}>
              Print
            </button>
            <button
              type="button"
              className="ai-btn ai-btn--orange"
              onClick={() => openContinuePopup(activeInvoice)}
              title="Create a new active job that stems off this work order"
            >
              Continue
            </button>
            {statusOf(activeInvoice) !== "Paid" && (
              <>
                <button
                  type="button"
                  className="ai-btn ai-btn--green"
                  onClick={() => openInvoiceEmailPopup(activeInvoice)}
                  disabled={sendingInvoiceId === activeInvoice.id}
                >
                  {sendingInvoiceId === activeInvoice.id
                    ? "Sending..."
                    : statusOf(activeInvoice) === "Sent"
                    ? "Resend Invoice"
                    : "Send Invoice"}
                </button>
                {statusOf(activeInvoice) !== "Sent" && (
                  <button type="button" className="ai-btn ai-btn--outline" onClick={() => openMarkSentPopup(activeInvoice)}>
                    Mark as Sent
                  </button>
                )}
                <button type="button" className="ai-btn ai-btn--teal" onClick={() => openInvoicePaidPopup(activeInvoice)}>
                  Invoice Paid
                </button>
              </>
            )}
            <button type="button" className="ai-btn ai-btn--red" onClick={() => deleteInvoice(activeInvoice)}>
              Delete
            </button>
          </div>
        </AiPopup>
      )}

      {/* ================= Edit popup ================= */}
      {isEditing && editedInvoice && (
        <AiPopup
          layer="main"
          width={1080}
          title="Edit Invoice"
          subtitle={[`Invoice #${editedInvoice.wo || editedInvoice.invoiceNumber || ""}`, editedInvoice.siteName]
            .filter(Boolean)
            .join(" · ")}
          onClose={savingInvoice ? undefined : closeDetails}
        >
          <div className="ai-edit-section ai-edit-section--blue">
            <h4>Bill To</h4>
            <div className="ai-form">
              <Field label="Site Name / Property">
                <input value={editedInvoice.siteName || ""} onChange={(e) => handleEditChange("siteName", e.target.value)} />
              </Field>
              <Field label="Bill To">
                <input value={editedInvoice.billTo || ""} onChange={(e) => handleEditChange("billTo", e.target.value)} />
              </Field>
              <Field label="Address">
                <input
                  value={editedInvoice.billingAddress || editedInvoice.address || ""}
                  onChange={(e) => handleEditChange("billingAddress", e.target.value)}
                />
              </Field>
              <Field label="City, State, Zip">
                <input value={editedInvoice.cityStateZip || ""} onChange={(e) => handleEditChange("cityStateZip", e.target.value)} />
              </Field>
              <Field label="c/o Management">
                <input value={editedInvoice.management || ""} onChange={(e) => handleEditChange("management", e.target.value)} />
              </Field>
              <Field label="Accounting Email" hint="Invoices are emailed here first.">
                <input
                  value={editedInvoice.accountingEmail || ""}
                  onChange={(e) => handleEditChange("accountingEmail", e.target.value)}
                />
              </Field>
              <Field label="Client Email">
                <input value={editedInvoice.clientEmail || ""} onChange={(e) => handleEditChange("clientEmail", e.target.value)} />
              </Field>
              <Field label="Manager Email">
                <input value={editedInvoice.managerEmail || ""} onChange={(e) => handleEditChange("managerEmail", e.target.value)} />
              </Field>
            </div>
          </div>

          <div className="ai-edit-section ai-edit-section--green">
            <h4>Invoice &amp; Service</h4>
            <div className="ai-form">
              <Field label="Terms">
                <select value={editedInvoice.terms || ""} onChange={(e) => handleEditChange("terms", e.target.value)}>
                  {withCurrent(TERMS_OPTIONS, editedInvoice.terms).map((term) => (
                    <option key={term} value={term}>
                      {term}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Invoice Date">
                <input
                  type="date"
                  value={editedInvoice.invoiceDate || todayKey()}
                  onChange={(e) => handleEditChange("invoiceDate", e.target.value)}
                />
              </Field>
              <Field label="Service Date">
                <input
                  type="date"
                  value={editedInvoice.serviceDate || ""}
                  onChange={(e) => handleEditChange("serviceDate", e.target.value)}
                />
              </Field>
              <Field label="Purchase Order">
                <input value={editedInvoice.purchaseOrder || ""} onChange={(e) => handleEditChange("purchaseOrder", e.target.value)} />
              </Field>
              <Field label="Product / Service">
                <select
                  value={editedInvoice.productService || ""}
                  onChange={(e) => handleEditChange("productService", e.target.value)}
                >
                  {withCurrent(productServiceOptions, editedInvoice.productService).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </Field>
              {editedInvoice.productService === "Other" && (
                <Field label="Other Product / Service">
                  <input
                    value={editedInvoice.customProductService || ""}
                    onChange={(e) => handleEditChange("customProductService", e.target.value)}
                  />
                </Field>
              )}
            </div>

            <h5 className="ai-subhead">Line Items</h5>
            {editRows ? (
              <>
                {editedInvoice.serviceRows.map((row, index) => (
                  <div key={row._key || index} className="ai-line">
                    <Field label={index === 0 ? "Service Description" : ""}>
                      <input
                        value={row.serviceDescription || ""}
                        onChange={(e) => updateServiceRow(index, "serviceDescription", e.target.value)}
                      />
                    </Field>
                    <Field label={index === 0 ? "Qty" : ""}>
                      <input
                        type="number"
                        step="0.01"
                        value={row.quantity ?? ""}
                        onChange={(e) => updateServiceRow(index, "quantity", e.target.value)}
                      />
                    </Field>
                    <Field label={index === 0 ? "Rate" : ""}>
                      <input
                        type="number"
                        step="0.01"
                        value={row.amount ?? ""}
                        onChange={(e) => updateServiceRow(index, "amount", e.target.value)}
                      />
                    </Field>
                    <Field label={index === 0 ? "Line Total" : ""}>
                      <input readOnly value={formatMoney(numberValue(row.quantity || 1) * numberValue(row.amount))} />
                    </Field>
                    <button
                      type="button"
                      className="ai-btn ai-btn--outline ai-btn--sm ai-line-remove"
                      disabled={editedInvoice.serviceRows.length <= 1}
                      onClick={() => removeServiceRow(index)}
                      title="Remove this line"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <div className="ai-btn-row">
                  <button type="button" className="ai-btn ai-btn--blue ai-btn--sm" onClick={addServiceRow}>
                    + Add Line
                  </button>
                </div>
              </>
            ) : (
              <div className="ai-form">
                <Field label="Qty">
                  <input type="number" value={editedInvoice.qty || 1} onChange={(e) => handleEditChange("qty", e.target.value)} />
                </Field>
                <Field label="Rate">
                  <input type="number" value={editedInvoice.rate || ""} onChange={(e) => handleEditChange("rate", e.target.value)} />
                </Field>
                <Field label="Amount">
                  <input type="number" value={editedInvoice.amount || ""} onChange={(e) => handleEditChange("amount", e.target.value)} />
                </Field>
                <div className="ai-field ai-field--end">
                  <button type="button" className="ai-btn ai-btn--outline ai-btn--sm" onClick={convertToLineItems}>
                    Switch to line items
                  </button>
                </div>
              </div>
            )}
            <div className="ai-total-banner">Invoice Total: {formatMoney(editedInvoice.amount)}</div>
          </div>

          <div className="ai-edit-section ai-edit-section--orange">
            <h4>Treatment Details</h4>
            <h5 className="ai-subhead">Chemical Products</h5>
            {(editedInvoice.chemicalProducts || []).map((product, index) => (
              <div key={index} className="ai-chem">
                <input
                  placeholder="Chemical / product"
                  value={product.chemical || ""}
                  onChange={(e) =>
                    updateChemicals(
                      editedInvoice.chemicalProducts.map((p, i) => (i === index ? { ...p, chemical: e.target.value } : p))
                    )
                  }
                />
                <input
                  placeholder="Qty"
                  value={product.qty || ""}
                  onChange={(e) =>
                    updateChemicals(
                      editedInvoice.chemicalProducts.map((p, i) => (i === index ? { ...p, qty: e.target.value } : p))
                    )
                  }
                />
                <button
                  type="button"
                  className="ai-btn ai-btn--outline ai-btn--sm"
                  onClick={() => updateChemicals(editedInvoice.chemicalProducts.filter((_, i) => i !== index))}
                >
                  Remove
                </button>
              </div>
            ))}
            <div className="ai-btn-row">
              <button
                type="button"
                className="ai-btn ai-btn--blue ai-btn--sm"
                onClick={() => updateChemicals([...(editedInvoice.chemicalProducts || []), { chemical: "", qty: "" }])}
              >
                + Add Chemical
              </button>
            </div>
            <div className="ai-form" style={{ marginTop: 12 }}>
              <Field label="Chemicals Used (as printed)" full>
                <textarea
                  rows="4"
                  value={editedInvoice.chemicalUsed || ""}
                  onChange={(e) => handleEditChange("chemicalUsed", e.target.value)}
                />
              </Field>
              <Field label="Pest Treated">
                <textarea rows="3" value={editedInvoice.pestTreated || ""} onChange={(e) => handleEditChange("pestTreated", e.target.value)} />
              </Field>
              <Field label="Areas Treated">
                <textarea rows="3" value={editedInvoice.areasTreated || ""} onChange={(e) => handleEditChange("areasTreated", e.target.value)} />
              </Field>
              <Field label="Equipment Used">
                <textarea rows="3" value={editedInvoice.equipmentUsed || ""} onChange={(e) => handleEditChange("equipmentUsed", e.target.value)} />
              </Field>
              <Field label="Weather">
                <textarea rows="2" value={editedInvoice.weather || ""} onChange={(e) => handleEditChange("weather", e.target.value)} />
              </Field>
              <Field label="Temperature">
                <input
                  placeholder="e.g. 72°F"
                  value={editedInvoice.temperature || ""}
                  onChange={(e) => handleEditChange("temperature", e.target.value)}
                />
              </Field>
              <Field label="Wind Speed">
                <textarea rows="2" value={editedInvoice.windSpeed || ""} onChange={(e) => handleEditChange("windSpeed", e.target.value)} />
              </Field>
              <Field label="Wind Direction">
                <textarea rows="2" value={editedInvoice.windDirection || ""} onChange={(e) => handleEditChange("windDirection", e.target.value)} />
              </Field>
              <Field label="Service Notes / Job Description (printed on the invoice)" full>
                <textarea
                  rows="6"
                  value={editedInvoice.jobDescription || ""}
                  onChange={(e) =>
                    setEditedInvoice((prev) => ({ ...prev, jobDescription: e.target.value, description: e.target.value }))
                  }
                />
              </Field>
            </div>
          </div>

          <div className="ai-edit-section ai-edit-section--purple">
            <h4>Notes &amp; Photos</h4>
            <div className="ai-form">
              <Field label="Notes" full hint="Shown on the invoice under Notes.">
                <textarea rows="4" value={editedInvoice.notes || ""} onChange={(e) => handleEditChange("notes", e.target.value)} />
              </Field>
            </div>
            <div className="ai-photo-grid">
              {editImages.map((item, index) => {
                const url = imageUrlOf(item);
                if (!url) return null;
                const name = (typeof item === "object" && item.name) || `Photo ${index + 1}`;
                return (
                  <div key={`${url}_${index}`} className="ai-photo">
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt={name} />
                    </a>
                    <span className="ai-photo-name">{name}</span>
                    <button type="button" className="ai-photo-x" onClick={() => removeInvoiceImage(index)} title="Remove photo">
                      ×
                    </button>
                  </div>
                );
              })}
              {editImages.length === 0 && <p className="ai-muted ai-small">No photos on this invoice.</p>}
            </div>
            <label className="ai-upload">
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={uploadingImages}
                onChange={(e) => {
                  uploadInvoiceImages(e.target.files);
                  e.target.value = "";
                }}
              />
              <span className="ai-btn ai-btn--blue ai-btn--sm">{uploadingImages ? "Uploading…" : "+ Add Photos"}</span>
            </label>
          </div>

          <div className="ai-edit-section ai-edit-section--yellow">
            <h4>Time, Signatures &amp; Submission</h4>
            <div className="ai-form">
              <Field
                label="Time In"
                hint={editedInvoice.timeInSource ? `Source: ${sourceLabel(editedInvoice.timeInSource)}` : ""}
              >
                <input
                  type="time"
                  value={editedInvoice.timeIn || ""}
                  onChange={(e) => setEditedInvoice((prev) => ({ ...prev, timeIn: e.target.value, timeInSource: "manual" }))}
                />
              </Field>
              <Field
                label="Time Out"
                hint={editedInvoice.timeOutSource ? `Source: ${sourceLabel(editedInvoice.timeOutSource)}` : ""}
              >
                <input
                  type="time"
                  value={editedInvoice.timeOut || ""}
                  onChange={(e) => setEditedInvoice((prev) => ({ ...prev, timeOut: e.target.value, timeOutSource: "manual" }))}
                />
              </Field>
              {(editedInvoice.checkInAt || editedInvoice.checkOutAt) && (
                <Field label="Check-in / Check-out (from the app)">
                  <input
                    readOnly
                    value={[formatDateTime(editedInvoice.checkInAt), formatDateTime(editedInvoice.checkOutAt)]
                      .filter(Boolean)
                      .join(" → ")}
                  />
                </Field>
              )}
              <Field label="Submitted Date">
                <input
                  type="date"
                  value={editedInvoice.submittedDate || ""}
                  onChange={(e) => handleEditChange("submittedDate", e.target.value)}
                />
              </Field>
              <Field label="Submitted By">
                <input value={editedInvoice.submittedBy || ""} onChange={(e) => handleEditChange("submittedBy", e.target.value)} />
              </Field>
              <div className="ai-field ai-field--end">
                <label className="ai-check">
                  <input
                    type="checkbox"
                    checked={!!editedInvoice.customerUnavailableToSign}
                    onChange={(e) => handleEditChange("customerUnavailableToSign", e.target.checked)}
                  />
                  Customer unavailable to sign
                </label>
              </div>
            </div>
            {(editedInvoice.technicianSignature || editedInvoice.customerSignature) && (
              <div className="ai-sigs">
                <div>
                  <strong>Technician Signature</strong>
                  {editedInvoice.technicianSignature ? (
                    <img src={editedInvoice.technicianSignature} alt="Technician signature" />
                  ) : (
                    <p className="ai-muted">None</p>
                  )}
                </div>
                <div>
                  <strong>Customer Signature</strong>
                  {editedInvoice.customerSignature ? (
                    <img src={editedInvoice.customerSignature} alt="Customer signature" />
                  ) : (
                    <p className="ai-muted">{editedInvoice.customerUnavailableToSign ? "Signature Not Necessary" : "None"}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="ai-btn-row ai-btn-row--end ai-sticky-actions">
            <button type="button" className="ai-btn ai-btn--outline" onClick={() => setIsEditing(false)} disabled={savingInvoice}>
              Cancel
            </button>
            <button type="button" className="ai-btn ai-btn--green" onClick={saveInvoice} disabled={savingInvoice || uploadingImages}>
              {savingInvoice ? "Saving…" : "Save Invoice"}
            </button>
          </div>
        </AiPopup>
      )}

      <ContinueJobPopup
        open={Boolean(continueInvoice)}
        sourceJob={continueSourceJob}
        nextWO={continueNextWO}
        loading={continueLoading}
        saving={continueSaving}
        error={continueError}
        onConfirm={confirmContinue}
        onCancel={closeContinuePopup}
      />

      {/* ================= Pricing (initial vs recurring) ================= */}
      {pricingInvoice && (
        <AiPopup title="Which amount is this invoice for?" onClose={() => setPricingInvoice(null)} width={560}>
          <p className="ai-muted">This invoice has both an initial fee and a recurring fee. Pick the one being billed.</p>
          <div className="ai-btn-row">
            <button type="button" className="ai-btn ai-btn--blue" onClick={() => selectInvoicePricing("initial")}>
              Initial Fee: {formatMoney(pricingInvoice.initialAmount)}
            </button>
            <button type="button" className="ai-btn ai-btn--green" onClick={() => selectInvoicePricing("recurring")}>
              {pricingInvoice.recurringFeeSchedule || pricingInvoice.feeSchedule || "Recurring"} Fee:{" "}
              {formatMoney(pricingInvoice.recurringInvoiceAmount ?? pricingInvoice.amount)}
            </button>
          </div>
        </AiPopup>
      )}

      {/* ================= Send email ================= */}
      {selectedInvoiceForEmail && (
        <AiPopup
          title="Send Invoice to Client"
          subtitle={`Invoice #${selectedInvoiceForEmail.wo || selectedInvoiceForEmail.invoiceNumber || ""} · ${formatMoney(
            selectedInvoiceForEmail.amount
          )}`}
          onClose={closeEmailPopup}
          width={820}
        >
          <div className="ai-form">
            <Field label="Accounting Email (To)">
              <input
                value={invoiceEmailData.accountingEmail}
                onChange={(e) => setInvoiceEmailData((prev) => ({ ...prev, accountingEmail: e.target.value }))}
                placeholder="accounting@email.com"
              />
            </Field>
            <Field label="CC" hint="The Priority office is always copied.">
              <input
                value={invoiceEmailData.cc}
                onChange={(e) => setInvoiceEmailData((prev) => ({ ...prev, cc: e.target.value }))}
                placeholder="email1@email.com, email2@email.com"
              />
            </Field>
            <Field label="BCC">
              <input
                value={invoiceEmailData.bcc}
                onChange={(e) => setInvoiceEmailData((prev) => ({ ...prev, bcc: e.target.value }))}
                placeholder="email1@email.com, email2@email.com"
              />
            </Field>
            <Field label="Subject">
              <input
                value={invoiceEmailData.subject}
                onChange={(e) => setInvoiceEmailData((prev) => ({ ...prev, subject: e.target.value }))}
              />
            </Field>
            <Field label="Message" full>
              <textarea
                rows="5"
                value={invoiceEmailData.message}
                onChange={(e) => setInvoiceEmailData((prev) => ({ ...prev, message: e.target.value }))}
              />
            </Field>
            <div className="ai-field ai-field--end">
              <label className="ai-check">
                <input
                  type="checkbox"
                  checked={!!invoiceEmailData.includePayLink}
                  onChange={(e) => setInvoiceEmailData((prev) => ({ ...prev, includePayLink: e.target.checked }))}
                />
                Include "Pay Online" Stripe link ({formatMoney(selectedInvoiceForEmail.amount)})
              </label>
            </div>
          </div>

          <div className="ai-section">
            <div className="ai-section-row">
              <strong>Photos to attach</strong>
              {availableInvoiceImages.length > 0 && (
                <span className="ai-muted ai-small">
                  {selectedInvoiceImages.length} of {availableInvoiceImages.length} selected ·{" "}
                  <button type="button" className="ai-link" onClick={() => setSelectedInvoiceImages(availableInvoiceImages)}>
                    All
                  </button>{" "}
                  ·{" "}
                  <button type="button" className="ai-link" onClick={() => setSelectedInvoiceImages([])}>
                    None
                  </button>
                </span>
              )}
            </div>
            {loadingEmailImages ? (
              <p className="ai-muted ai-small">Loading photos…</p>
            ) : availableInvoiceImages.length === 0 ? (
              <p className="ai-muted ai-small">No photos on this invoice or its job.</p>
            ) : (
              <div className="ai-pick-grid">
                {availableInvoiceImages.map((img, index) => {
                  const checked = selectedInvoiceImages.some((s) => s.url === img.url);
                  return (
                    <label key={img.url} className={`ai-pick${checked ? " is-checked" : ""}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setSelectedInvoiceImages((prev) =>
                            e.target.checked ? [...prev, img] : prev.filter((s) => s.url !== img.url)
                          )
                        }
                      />
                      <img src={img.url} alt={img.name || `Photo ${index + 1}`} />
                      <span>
                        {img.source === "invoice" ? "Invoice photo" : "Job photo"}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="ai-btn-row ai-btn-row--end">
            <button
              type="button"
              className="ai-btn ai-btn--outline"
              onClick={closeEmailPopup}
              disabled={sendingInvoiceId === selectedInvoiceForEmail.id}
            >
              Cancel
            </button>
            <button
              type="button"
              className="ai-btn ai-btn--green"
              onClick={sendInvoiceEmail}
              disabled={sendingInvoiceId === selectedInvoiceForEmail.id}
            >
              {sendingInvoiceId === selectedInvoiceForEmail.id ? "Sending..." : "Send Email"}
            </button>
          </div>
        </AiPopup>
      )}

      {/* ================= Mark as sent (not by email) ================= */}
      {markSentInvoice && (
        <AiPopup
          title="Record invoice as sent"
          subtitle={`Invoice #${markSentInvoice.wo || markSentInvoice.invoiceNumber || markSentInvoice.id}${
            markSentInvoice.siteName ? ` — ${markSentInvoice.siteName}` : ""
          } · ${formatMoney(markSentInvoice.amount)}`}
          onClose={markSentSaving ? undefined : () => setMarkSentInvoice(null)}
          width={480}
        >
          <p className="ai-muted ai-small">For invoices that did not go out by email. No email is sent from here.</p>
          <div className="ai-form ai-form--single">
            <Field label="Date sent">
              <input
                type="date"
                value={markSentDate}
                max={localDayKey(new Date())}
                onChange={(e) => setMarkSentDate(e.target.value)}
              />
            </Field>
            <Field label="How it was sent">
              <select value={markSentMethod} onChange={(e) => setMarkSentMethod(e.target.value)}>
                {SEND_METHODS.filter((m) => m !== "Email").map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Note (optional)">
              <input
                type="text"
                value={markSentNote}
                placeholder="Tracking #, who it went to, etc."
                onChange={(e) => setMarkSentNote(e.target.value)}
              />
            </Field>
          </div>
          <div className="ai-btn-row ai-btn-row--end">
            <button type="button" className="ai-btn ai-btn--outline" disabled={markSentSaving} onClick={() => setMarkSentInvoice(null)}>
              Cancel
            </button>
            <button type="button" className="ai-btn ai-btn--green" disabled={markSentSaving || !markSentDate} onClick={saveMarkSent}>
              {markSentSaving ? "Saving…" : "Record as sent"}
            </button>
          </div>
        </AiPopup>
      )}

      {/* ================= Invoice paid ================= */}
      {invoiceBeingPaid && (
        <AiPopup
          title="Invoice Paid"
          subtitle={`Invoice #${getInvoiceNumber(invoiceBeingPaid)} · ${invoiceBeingPaid.siteName || ""}`}
          onClose={closePaidPopup}
          width={760}
        >
          <div className="ai-form">
            <Field label="Payment Type">
              <select value={invoicePaymentMode} onChange={(e) => setInvoicePaymentMode(e.target.value)}>
                <option value="single">Single Invoice</option>
                <option value="split">Split Check Across Invoices</option>
              </select>
            </Field>
            <Field label="Vendor">
              <select value={paidVendor} onChange={(e) => setPaidVendor(e.target.value)}>
                {vendorOptions.map((vendor) => (
                  <option key={vendor} value={vendor}>
                    {vendor}
                  </option>
                ))}
              </select>
            </Field>
            {paidVendor === "Other" && (
              <Field label="Other Vendor">
                <input value={paidVendorOther} onChange={(e) => setPaidVendorOther(e.target.value)} />
              </Field>
            )}
            <Field label="Check Number">
              <input value={paidCheckNumber} onChange={(e) => setPaidCheckNumber(e.target.value)} />
            </Field>
            <Field label="Check Date">
              <input type="date" value={paidCheckDate} onChange={(e) => setPaidCheckDate(e.target.value)} />
            </Field>
            <Field label="Reference">
              <input value={paidReference} onChange={(e) => setPaidReference(e.target.value)} placeholder="Optional reference" />
            </Field>
            <Field label="Check Amount">
              <input type="number" value={paidCheckAmount} onChange={(e) => setPaidCheckAmount(e.target.value)} />
            </Field>
          </div>

          {invoicePaymentMode === "split" && (
            <div className="ai-section">
              <strong>Split Check Across Invoices</strong>
              <div className="ai-table-wrap" style={{ marginTop: 8 }}>
                <table className="ai-table ai-table--compact">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Invoice #</th>
                      <th>Property</th>
                      <th>Invoice Total</th>
                      <th>Paid Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {splitPaidRows.map((row, index) => (
                      <tr key={`${row.invoiceId}-${row.wo}`}>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!row.selected}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSplitPaidRows((prev) =>
                                prev.map((r, i) =>
                                  i === index ? { ...r, selected: checked, amount: checked ? r.amount || r.invoiceTotal : "" } : r
                                )
                              );
                            }}
                          />
                        </td>
                        <td>{row.wo}</td>
                        <td>{row.siteName}</td>
                        <td>{formatMoney(row.invoiceTotal)}</td>
                        <td>
                          <input
                            type="number"
                            className="ai-cell-input"
                            value={row.amount}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSplitPaidRows((prev) => prev.map((r, i) => (i === index ? { ...r, amount: value } : r)));
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="ai-btn-row ai-btn-row--end">
            <button type="button" className="ai-btn ai-btn--outline" onClick={closePaidPopup} disabled={savingPayment}>
              Cancel
            </button>
            <button type="button" className="ai-btn ai-btn--green" onClick={saveInvoicePayment} disabled={savingPayment}>
              {savingPayment ? "Saving…" : "Save Payment"}
            </button>
          </div>
        </AiPopup>
      )}
    </div>
  );
};

export default ApprovedInvoices;
